import { useIntl } from "react-intl";
import { Dialog, DialogActions, DialogContent, Stack, Typography, DialogProps } from "@mui/material";

import { ReactComponent as EnboqLogo } from "assets/svg/Enboq-Logo-NoPayoff-Svg.svg";
import { ButtonContained, ButtonContainedProps } from "components/button/ButtonContained";
import { ButtonOutlined, ButtonOutlinedProps } from "components/button/ButtonOutlined";
import { useEffect, useRef } from "react";
import { autoSetViewBox } from "utils/helpers/svg";

interface ConfirmDialogProps extends DialogProps {
	buttonContainedProps?: ButtonContainedProps;
	buttonOutlinedProps?: ButtonOutlinedProps;
	useRemUnit?: boolean;
	title: string;
	subtitle?: string;
	confirmText?: string;
	cancelText?: string;
	// note: for an admin's organization logo, get it from useAdminAuthContext in organization.logoURI.
	// note: for a user's organization logo, get it from fetchUserFunnel in headerDataObj.companyLogo.
	logo?: string;
}

export const ConfirmDialog = ({
	buttonContainedProps,
	buttonOutlinedProps,
	useRemUnit = false,
	title,
	subtitle,
	confirmText,
	cancelText,
	logo,
	...props
}: ConfirmDialogProps) => {
	const intl = useIntl();

	const defaultConfirmText = intl.formatMessage({ id: "DASHBOARD.content.YES" });
	const defaultCancelText = intl.formatMessage({ id: "DASHBOARD.content.NO" });

	const svgRef = useRef<SVGSVGElement>(null);

	useEffect(() => {
		const timeout = setTimeout(() => {
			autoSetViewBox(svgRef.current);
		}, 50);

		return () => clearTimeout(timeout);
	}, []);

	return (
		<Dialog
			sx={{
				"& .MuiPaper-root": {
					width: "60%",
					borderRadius: useRemUnit ? "0.8rem" : "8px",
				},
			}}
			{...props}
		>
			<DialogContent sx={{ p: useRemUnit ? "4.8rem 3.2rem" : "48px 32px", pb: 0 }}>
				<Stack alignItems="center" gap={useRemUnit ? "0.8rem" : "8px"}>
					{!!logo ? (
						<img
							src={logo}
							alt="Company logo"
							width={useRemUnit ? "10rem" : "100px"}
							style={{
								width: useRemUnit ? "10rem" : "100px",
								marginBottom: useRemUnit ? "1.6rem" : "16px",
							}}
						/>
					) : (
						<EnboqLogo
							ref={svgRef}
							height={useRemUnit ? "3rem" : "30"}
							style={{ marginBottom: useRemUnit ? "1.6rem" : "16px" }}
						/>
					)}
					<Typography
						id="dialog-text"
						textAlign="center"
						fontWeight={500}
						fontSize={useRemUnit ? "1.8rem" : "18px"}
						color="grey.600"
					>
						{title}
					</Typography>
					<Typography textAlign="center" fontSize={useRemUnit ? "1.6rem" : "16px"} color="#616161">
						{subtitle}
					</Typography>
				</Stack>
			</DialogContent>
			<DialogActions
				sx={{
					justifyContent: "center",
					gap: useRemUnit ? "1.6rem" : "16px",
					p: useRemUnit ? "3.2rem" : "32px",
					pb: useRemUnit ? "4.8rem" : "48px",
				}}
			>
				<ButtonOutlined
					id="button-no"
					data-useremunit={useRemUnit ? "true" : "false"}
					sx={{
						...(!cancelText
							? {
									width: useRemUnit ? "12rem" : "120px",
									fontWeight: 600,
									fontSize: useRemUnit ? "1.8rem" : "18px",
							  }
							: { fontWeight: 600, fontSize: useRemUnit ? "1.8rem" : "18px" }),
					}}
					{...buttonOutlinedProps}
				>
					{cancelText || defaultCancelText}
				</ButtonOutlined>
				<ButtonContained
					id="button-yes"
					data-useremunit={useRemUnit ? "true" : "false"}
					sx={{
						...(!confirmText
							? { width: useRemUnit ? "12rem" : "120px", fontSize: useRemUnit ? "1.8rem" : "18px" }
							: { fontSize: useRemUnit ? "1.8rem" : "18px" }),
					}}
					{...buttonContainedProps}
				>
					{confirmText || defaultConfirmText}
				</ButtonContained>
			</DialogActions>
		</Dialog>
	);
};
